import { Options } from '@/components/mi-dialog/MiDialog';
import DataTable, { TableOptions, TableVisibility, DatatableInterface } from '@/components/data-table/DataTable';
import { Component, Prop, VModel } from 'vue-property-decorator';
import AbstractDatatable from '@/components/CustomDatatable/AbstractDatatable';
import { PurchaseOrderItem, PurchaseOrderItemTableHeader } from '@/models/PurchaseOrderItem';
import { AxiosError } from 'axios';
import ErrorHandler from '@/support/ErrorHandler';
import { formatDate } from '@/support/String';

@Component<PurchaseOrderItemDatatable>({})
export default class PurchaseOrderItemDatatable extends AbstractDatatable implements DatatableInterface {

  // #region @VModel

  @VModel()
  protected internalValue!: PurchaseOrderItem[];

  // #endregion

  // #region @Props

  @Prop({ default: false })
  protected canEditOrderItems!: boolean;

  @Prop({ default: undefined })
  protected received!: boolean;

  @Prop({ })
  protected purchaseOrderId!: string;

  @Prop({ default: undefined })
  protected totalsReceivedAndNotReceived!: boolean;

  @Prop({ default: false })
  protected hasCheckboxes!: boolean;

  // #endregion

  // #region @Refs
  // #endregion

  // #region Props

  protected defaultPage = 1;

  protected isShowingEditPurchaseOrderItemDialog = false;

  protected selectedPurchaseOrderItem: null | PurchaseOrderItem = null;

  protected tableVisibility: TableVisibility = {
    checkboxes: this.hasCheckboxes,
    limit: true,
    pagination: true,
    search: false,
    title: false,
    total: ! this.totalsReceivedAndNotReceived,
  };

  // #endregion

  // #region Plugin props
  // #endregion

  // #region Lifecycle hooks / Init
  // #endregion

  // #region Handlers
  // #endregion

  // #region Helpers
  // #endregion

  // #region API Requests
  // #endregion

  // #region Getters & Setters

  protected get page() {
    const result = localStorage.getItem(`${this.$route.name}_page`);
    return result ? Number(result) : this.defaultPage;
  }

  protected set page(page: number) {
    localStorage.setItem(`${this.$route.name}_page`, page.toString());
    this.defaultPage = page;
  }

  protected get tableOptions(): TableOptions {
    return {
      model: new PurchaseOrderItem()
        .filter({
          purchase_order: this.$route.params.id,
          ...this.received !== undefined
            ? {
              received: this.received
                ? 1
                : 0
              }
            : {},
          ...this.totalsReceivedAndNotReceived !== undefined
              ? {
                totalsReceivedNotReceived: true,
                received: 1, // FIXME: REMOVE THIS LINE WHEN TOTALS IS FUNCTIONING WELL
              }
              : {}
        })
        .include(['product-variant', 'totals']),
      name: {
        singular: 'Order item',
        plural: 'Order items'
      },
      showTotals: true,
      headers: [
        PurchaseOrderItemTableHeader.image,
        ...this.received === undefined && this.totalsReceivedAndNotReceived === undefined
          ? [
            PurchaseOrderItemTableHeader.merged_name_and_general_details,
            PurchaseOrderItemTableHeader.upc,
            {
              text: 'Is Dummy upc',
              value: 'is_dummy_upc',
              transform: (is_dummy_upc: boolean) => is_dummy_upc ? 'Yes' : 'No',
            },
            PurchaseOrderItemTableHeader.category,
            PurchaseOrderItemTableHeader.status,
            PurchaseOrderItemTableHeader.merged_color_and_size,
            PurchaseOrderItemTableHeader.merged_supplier_color_and_size,
            PurchaseOrderItemTableHeader.season,
            {
              text: 'Expected At',
              value: 'expected_at',
              transform: (expected_at: string) => formatDate(expected_at, 'dd-LL-yyyy'),
            },
            PurchaseOrderItemTableHeader.buy_price,
            PurchaseOrderItemTableHeader.total_price,
            PurchaseOrderItemTableHeader.quantity_received,
            PurchaseOrderItemTableHeader.date_received_at,
          ]
          : [
            this.totalsReceivedAndNotReceived === undefined
              ? PurchaseOrderItemTableHeader.merged_name_and_general_details
              : {
                value: 'merged_name_and_general_details',
                text: '',
                width: '100%',
              },
            PurchaseOrderItemTableHeader.quantity_ordered,
            PurchaseOrderItemTableHeader.quantity_outstanding,
            PurchaseOrderItemTableHeader.quantity_received,
            PurchaseOrderItemTableHeader.buy_price,
            PurchaseOrderItemTableHeader.total_price,
            PurchaseOrderItemTableHeader.total_price_outstanding,
            PurchaseOrderItemTableHeader.total_price_received,
          ]
      ],
      actions: [
        {
          type: 'view',
          label: 'view',
          icon: 'remove_red_eye',
          action: '/varianten/{product_variant.id}',
          tooltip: 'Show product variant'
        },
        ...this.canEditOrderItems
          ? [
            {
              type: 'edit',
              label: 'edit',
              icon: 'edit',
              action: (datatable: DataTable, PurchaseOrderItem: PurchaseOrderItem) => {
                this.selectedPurchaseOrderItem = PurchaseOrderItem;
                this.isShowingEditPurchaseOrderItemDialog = true;
              },
              tooltip: 'Edit product variant'
            },
            {
              type: 'delete',
              label: 'delete',
              icon: 'delete',
              action: (datatable: DataTable, PurchaseOrderItem: PurchaseOrderItem) => {
                this.selectedPurchaseOrderItem = PurchaseOrderItem;
                this.$store.dispatch('openDialog', this.deletePurchaseOrderItemDialogOptions);
              },
            },
          ]
          : []
      ],
      filters: [],
    }
  }

  protected get deletePurchaseOrderItemDialogOptions(): Options {
    return {
      title: 'Delete item from purchase order',
      text: 'Are you sure you wish to delete this item from the purchase order?',
      type: 'warning',
      buttons: {
        confirm: {
          text: 'Delete',
          action: () => {
            if (!this.selectedPurchaseOrderItem) {
              return;
            }

            this.selectedPurchaseOrderItem
              .delete()
              .then((response: any) => {
                if (response) {
                  this.refresh(true);
                  this.selectedPurchaseOrderItem = null;
                  this.$emit('refresh');
                }
              })
              .catch((error: AxiosError) => {
                ErrorHandler.network(error);
              });
          },
        },
        cancel: {
          text: 'Cancel',
          action: () => {
            this.selectedPurchaseOrderItem = null;
          },
        },
      },
    };
  }

  // #endregion

  // #endregion

  // #region @Watchers
  // #endregion
}
